import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import Select from 'react-select';
import { MachineInfo, MachineService } from '../api/api_pb';
import { useClient } from '../connect_api';
import { toast } from 'react-toastify';

interface MachineOption {
  value: string;
  label: string;
  machine: MachineInfo;
}

interface MachineMultiSelectProps {
  selectedMachines: string[];
  onSelectionChange: (machineUuids: string[]) => void;
  disabled?: boolean;
}

const MachineMultiSelect: React.FC<MachineMultiSelectProps> = ({
  selectedMachines,
  onSelectionChange,
  disabled = false
}) => {
  const [machines, setMachines] = useState<MachineInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const machineClient = useClient(MachineService);

  useEffect(() => {
    let isMounted = true;
    
    const fetchMachines = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await machineClient.listMachines({});
        if (isMounted) {
          setMachines(response.machines);
        }
      } catch (err) {
        if (isMounted) {
          setError('Failed to load machines');
          console.error('Error fetching machines:', err);
          toast.error('Failed to load machines');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchMachines();
    
    return () => {
      isMounted = false;
    };
  }, [machineClient]);

  const machineOptions: MachineOption[] = machines.map(machine => ({
    value: machine.uuid,
    label: machine.name + (machine.address ? ` (${machine.address})` : ''),
    machine
  }));

  const selectedOptions = machineOptions.filter(option => 
    selectedMachines.includes(option.value)
  );

  const handleChange = (selectedOptions: readonly MachineOption[] | null) => {
    const machineUuids = selectedOptions ? selectedOptions.map(option => option.value) : [];
    onSelectionChange(machineUuids);
  };

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      minHeight: '42px',
      borderColor: state.isFocused ? '#0d6efd' : '#dee2e6',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(13, 110, 253, 0.25)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#0d6efd' : '#adb5bd'
      }
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: '#6c757d',
      fontStyle: 'italic'
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999
    }),
    menuList: (provided: any) => ({
      ...provided,
      maxHeight: '200px'
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#f8f9fa' : 'white',
      color: state.isSelected ? 'white' : '#495057',
      fontSize: '14px',
      padding: '8px 12px',
      cursor: 'pointer'
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: '#e7f3ff',
      border: '1px solid #bee5eb'
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: '#0c5460',
      fontSize: '13px'
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: '#6c757d',
      ':hover': {
        backgroundColor: '#dc3545',
        color: 'white',
      },
    }),
  };

  return (
    <Form.Group>
      <Form.Label className="text-sm font-medium text-gray-700">
        Filter by Machines
      </Form.Label>
      <Select
        isMulti
        isSearchable={true}
        options={machineOptions}
        value={selectedOptions}
        onChange={handleChange}
        placeholder="Type to search and select machines..."
        isLoading={isLoading}
        isDisabled={disabled || isLoading}
        styles={customStyles}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        noOptionsMessage={({ inputValue }) => 
          error ? error : inputValue ? `No machines found matching "${inputValue}"` : 'No machines available'
        }
        loadingMessage={() => 'Loading machines...'}
        className="react-select-container"
        classNamePrefix="react-select"
        filterOption={(option, inputValue) => {
          const machine = option.data.machine;
          const searchTerm = inputValue.toLowerCase();
          return (
            machine.name.toLowerCase().includes(searchTerm) ||
            Boolean(machine.address && machine.address.toLowerCase().includes(searchTerm))
          );
        }}
        menuIsOpen={undefined}
        menuShouldBlockScroll={false}
      />
      {error && (
        <Form.Text className="text-danger">
          {error}
        </Form.Text>
      )}
      {!error && (
        <Form.Text className="text-muted">
          Type to search by machine name or address
        </Form.Text>
      )}
    </Form.Group>
  );
};

export default MachineMultiSelect;